import {
  formatCurrency,
  formatPercent,
  FRAMES_DISCOUNTS_RULE_TYPE,
  isSentinel,
  sentinelLabel,
} from "./shared";

const COVERED_IN_FULL_AFTER_COPAY = "Covered in Full After Copay";
const allowanceCopayWaived = " allowance (copay waived)";

const getValueWithSuffixOrEmpty = (suffix: string, value?: string): string =>
  value ? `${value}${suffix}` : "";

/**
 * Currency for an amount the carrier actually stated, blank when it did not.
 *
 * `formatCurrency` coerces with `Number`, which reads null and any blank string as zero, so a
 * benefit the carrier left unstated renders as $0 — indistinguishable from one genuinely priced
 * at nothing. Blank covers whitespace as well as "", since an amount only ever reaches here as
 * a number or a serialization of one. A real 0 still formats, so only absence is suppressed.
 * Matches the absent-value contract of `toFiniteNumber` in ./shared.
 */
const currencyIfPresent = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && value.trim() === "") return "";

  return formatCurrency(value);
};

export type VisionFieldFormatter = (row: Record<string, unknown>) => string;

/** Simple copay field: dollarAmount → $X. */
export const copayTyped =
  (typeKey: string, amountKey: string): VisionFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);
    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

/**
 * Lens benefit with copay + allowance amounts.
 * Used for single/bifocal/trifocal/lenticular (CopayFullAllowance type).
 */
export const lensFullAllowance =
  (
    typeKey: string,
    copayKey: string,
    allowanceKey: string,
  ): VisionFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);
    if (type === "coveredInFull") return "Covered in Full";
    if (type === "coveredInFullAfterCopay") {
      return COVERED_IN_FULL_AFTER_COPAY;
    }
    if (type === "dollarAmountAllowance") {
      const allowance = formatCurrency(row[allowanceKey]);
      return allowance ? `${allowance} allowance` : "";
    }
    if (type === "dollarAmountAllowanceWithCopay") {
      const copay = formatCurrency(row[copayKey]);
      return copay ? `${copay} copay` : "";
    }
    return "";
  };

/**
 * Copay + allowance lens benefit (RfpVisionCopayAllowanceRuleType).
 * Used for Standard Progressive and Premium Progressive.
 */
export const copayAllowance =
  (
    typeKey: string,
    copayKey: string,
    allowanceKey: string,
  ): VisionFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);
    if (type === "coveredInFullAfterCopay") {
      return COVERED_IN_FULL_AFTER_COPAY;
    }
    if (type === "dollarAmountAllowance") {
      const allowance = formatCurrency(row[allowanceKey]);
      return getValueWithSuffixOrEmpty(" allowance", allowance);
    }
    if (type === "dollarAmountAllowanceWithoutCopay") {
      const allowance = formatCurrency(row[allowanceKey]);
      return getValueWithSuffixOrEmpty(allowanceCopayWaived, allowance);
    }
    if (type === "dollarAmountAllowanceWithCopay") {
      const copay = formatCurrency(row[copayKey]);
      return getValueWithSuffixOrEmpty(" copay", copay);
    }
    return "";
  };

/** Progressive Lenses: covered-in-full-after-copay, or a plain (unsuffixed) allowance. */
export const formatProgressiveLenses: VisionFieldFormatter = (row) => {
  const type = row["progressiveLensesType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "coveredInFullAfterCopay") return COVERED_IN_FULL_AFTER_COPAY;
  if (type === "dollarAmountAllowance") {
    return formatCurrency(row["progressiveLensesAllowanceAmount"]) || "";
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = formatCurrency(row["progressiveLensesAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" (copay waived)", allowance);
  }
  return "";
};

/** Contacts (Med Nec): covered-in-full-after-copay, covered-in-full, or an allowance amount. */
export const formatContactLensMedNec: VisionFieldFormatter = (row) => {
  const type = row["contactLensMedNecType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "coveredInFullAfterCopay") return COVERED_IN_FULL_AFTER_COPAY;
  if (type === "coveredInFull") return "Covered in Full";
  if (type === "dollarAmountAllowance") {
    const allowance = formatCurrency(row["contactLensMedNecAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" allowance", allowance);
  }
  return "";
};

/** Conventional Contacts (RfpVisionConventionalContactRuleType): allowance, optionally with an additional percentage off balance. */
export const formatConventionalContact: VisionFieldFormatter = (row) => {
  const type = row["conventionalContactType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "dollarAmountAllowance") {
    const allowance = formatCurrency(row["conventionalContactAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" allowance", allowance);
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = formatCurrency(row["conventionalContactAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(allowanceCopayWaived, allowance);
  }
  if (type === "dollarAmountAllowanceWithAdditionalDiscount") {
    const allowance = formatCurrency(row["conventionalContactAllowanceAmount"]);
    if (!allowance) return "";
    const discount = formatPercent(
      row["conventionalContactAdditionalDiscountAmount"],
    );
    return discount
      ? `${allowance} allowance + ${discount} off balance`
      : `${allowance} allowance`;
  }
  return "";
};

/** Disposable Contacts (RfpVisionDisposableContactRuleType): allowance, optionally with copay waived or an "in lieu of glasses" copay. */
export const formatDisposableContact: VisionFieldFormatter = (row) => {
  const type = row["disposableContactType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "dollarAmountAllowance") {
    const allowance = formatCurrency(row["disposableContactAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" allowance", allowance);
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = formatCurrency(row["disposableContactAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(allowanceCopayWaived, allowance);
  }
  if (type === "allowanceInLieuOfGlasses") {
    const allowance = formatCurrency(row["disposableContactAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(
      " allowance in lieu of glasses",
      allowance,
    );
  }
  if (type === "allowanceInLieuOfGlassesWithCopay") {
    const allowance = formatCurrency(row["disposableContactAllowanceAmount"]);
    if (!allowance) return "";
    const copay = formatCurrency(row["disposableContactCopayAmount"]);
    return copay
      ? `${allowance} allowance in lieu of glasses after ${copay} copay`
      : `${allowance} allowance in lieu of glasses`;
  }
  return "";
};

export const formatContactLensElective: VisionFieldFormatter = (row) => {
  const type = row["contactLensElectiveType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (
    type === "dollarAmountAllowance" ||
    type === "dollarAmountAllowanceWithCopay"
  ) {
    const allowance = formatCurrency(row["contactLensElectiveAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" allowance", allowance);
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = formatCurrency(row["contactLensElectiveAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(allowanceCopayWaived, allowance);
  }
  return "";
};

export const formatContactLensExam: VisionFieldFormatter = (row) => {
  const type = row["contactLensExamType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "coveredInFullAfterCopay") {
    return COVERED_IN_FULL_AFTER_COPAY;
  }
  if (
    type === "dollarAmountAllowance" ||
    type === "dollarAmountAllowanceWithCopay"
  ) {
    const allowance = currencyIfPresent(row["contactLensExamAllowanceAmount"]);
    if (allowance) return allowance;
    // Carriers state this benefit as a maximum copay and no allowance — MetLife covers the
    // contact lens fitting in full after a copay of up to $60 — so the allowance arrives
    // null and the copay carries the value. Without this the row reads $0, which asserts a
    // benefit the member pays for is free.
    if (type === "dollarAmountAllowanceWithCopay") {
      const copay = currencyIfPresent(row["contactLensExamCopayAmount"]);
      return getValueWithSuffixOrEmpty(" copay", copay);
    }

    return "";
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = currencyIfPresent(row["contactLensExamAllowanceAmount"]);
    return getValueWithSuffixOrEmpty(" (copay waived)", allowance);
  }
  return "";
};

const framesDiscountSuffix = (row: Record<string, unknown>): string => {
  const type = row["additionalDiscountsOnFramesType"];
  if (type === FRAMES_DISCOUNTS_RULE_TYPE.percentage) {
    const percent = formatPercent(row["additionalDiscountsOnFramesAmount"]);
    return percent ? ` + ${percent} off balance` : "";
  }
  if (type === FRAMES_DISCOUNTS_RULE_TYPE.dollar) {
    const amount = formatCurrency(row["additionalDiscountsOnFramesAmount"]);
    return amount ? ` + additional ${amount}` : "";
  }
  return "";
};

export const formatFramesAfterCopay: VisionFieldFormatter = (row) => {
  const type = row["standardFramesType"];
  if (isSentinel(type)) return sentinelLabel(type);

  if (
    type === "dollarAmountAllowance" ||
    type === "dollarAmountAllowanceWithCopay"
  ) {
    const allowance = formatCurrency(row["standardFramesAllowanceAmount"]);
    return allowance ? `${allowance}${framesDiscountSuffix(row)}` : "";
  }
  if (type === "dollarAmountAllowanceWithoutCopay") {
    const allowance = formatCurrency(row["standardFramesAllowanceAmount"]);
    return allowance
      ? `${allowance} (copay waived)${framesDiscountSuffix(row)}`
      : "";
  }
  return "";
};
